/**
 * SubTaskList - json-render component
 * 
 * Displays a hierarchical list of sub-tasks with status indicators.
 * Used during plan execution to show step progress.
 */

import { useState } from 'react';
import {
    Circle,
    Loader2,
    CheckCircle2,
    XCircle,
    Clock,
    ChevronRight,
    ChevronDown
} from 'lucide-react';
import { cn } from '@pagespeedify/ui';

export interface SubTaskListItem {
    id: string;
    title: string;
    status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'waiting' | 'skipped';
    suffix?: string;
    children?: SubTaskListItem[];
}

export interface SubTaskListProps {
    phaseTitle?: string;
    subTasks: SubTaskListItem[];
}

interface StatusConfig {
    icon: typeof Circle;
    color: string;
    bgColor: string;
    label: string;
    animate?: boolean;
}

const statusConfig: Record<SubTaskListItem['status'], StatusConfig> = {
    pending: {
        icon: Circle,
        color: 'text-slate-400',
        bgColor: 'bg-slate-100 dark:bg-slate-800',
        label: 'Pending'
    },
    in_progress: {
        icon: Loader2,
        color: 'text-blue-500',
        bgColor: 'bg-blue-50 dark:bg-blue-950',
        label: 'In Progress',
        animate: true
    },
    completed: {
        icon: CheckCircle2,
        color: 'text-green-500',
        bgColor: 'bg-green-50 dark:bg-green-950',
        label: 'Completed'
    },
    failed: {
        icon: XCircle,
        color: 'text-red-500',
        bgColor: 'bg-red-50 dark:bg-red-950',
        label: 'Failed'
    },
    waiting: {
        icon: Clock,
        color: 'text-amber-500',
        bgColor: 'bg-amber-50 dark:bg-amber-950',
        label: 'Waiting'
    },
    skipped: {
        icon: Circle,
        color: 'text-slate-300',
        bgColor: 'bg-slate-50 dark:bg-slate-900',
        label: 'Skipped'
    }
};

function SubTaskItem({ task, depth = 0 }: { task: SubTaskListItem; depth?: number }) {
    const [isExpanded, setIsExpanded] = useState(true);
    const config = statusConfig[task.status] || statusConfig.pending;
    const Icon = config.icon;
    const hasChildren = task.children && task.children.length > 0;

    return (
        <div className="w-full">
            <div
                className={cn(
                    "flex items-center gap-2 py-1.5 px-2 rounded-md transition-colors",
                    config.bgColor,
                    hasChildren && "cursor-pointer hover:opacity-80"
                )}
                style={{ paddingLeft: `${depth * 16 + 8}px` }}
                onClick={() => hasChildren && setIsExpanded(!isExpanded)}
            >
                {hasChildren && (
                    <span className="text-slate-400">
                        {isExpanded ? (
                            <ChevronDown className="h-3 w-3" />
                        ) : (
                            <ChevronRight className="h-3 w-3" />
                        )}
                    </span>
                )}
                <Icon
                    className={cn(
                        "h-4 w-4 shrink-0",
                        config.color,
                        config.animate && "animate-spin"
                    )}
                />
                <span className={cn(
                    "text-sm flex-1",
                    task.status === 'skipped' && "line-through opacity-60"
                )}>
                    {task.title}
                </span>
                {task.suffix && (
                    <span className="text-xs text-slate-500">{task.suffix}</span>
                )}
            </div>

            {hasChildren && isExpanded && (
                <div className="mt-1 space-y-1">
                    {task.children!.map((child) => (
                        <SubTaskItem
                            key={child.id}
                            task={child}
                            depth={depth + 1}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

export default function SubTaskList({ phaseTitle, subTasks }: SubTaskListProps) {
    if (!subTasks || subTasks.length === 0) {
        return null;
    }

    const completedCount = subTasks.filter(t => t.status === 'completed').length;
    const totalCount = subTasks.length;

    return (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
            {phaseTitle && (
                <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                    <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            {phaseTitle}
                        </h4>
                        <span className="text-xs text-slate-500">
                            {completedCount}/{totalCount} completed
                        </span>
                    </div>
                </div>
            )}
            <div className="p-2 space-y-1">
                {subTasks.map((task) => (
                    <SubTaskItem key={task.id} task={task} />
                ))}
            </div>
        </div>
    );
}
